import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Rocket, X } from "lucide-react";
import AdsWizard from "@/components/AdsWizard";

/**
 * Shown right after a user publishes a post with image/video/banner.
 * One-click path to open the Ads Manager wizard with the post's media pre-filled.
 */
export default function BoostPostDialog({ open, onOpenChange, post }) {
  const [wizardOpen, setWizardOpen] = useState(false);

  const media = post?.image_url || post?.video_url || post?.media_url;
  const isVideo = !!post?.video_url;

  const prefill = post && media ? {
    format: isVideo ? "video" : "image",
    creative: {
      title: (post.content || "Publicação FUTURO PDV").slice(0, 40),
      primary_text: (post.content || "Descubra mais no FUTURO PDV").slice(0, 280),
      description: "",
      media: [{ url: media, type: isVideo ? "video" : "image", aspect_ratio: "1:1" }],
    },
    cta: "saiba_mais",
    destination_url: (typeof window !== "undefined" ? `${process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin}/feed#post-${post.post_id}` : "https://futuropdv.com"),
    name: `Impulso — ${(post.content || "post").slice(0, 30)}`,
  } : null;

  // IMPORTANT: keep both Dialogs permanently mounted and controlled solely by their `open` prop.
  // Toggling the Dialog condition alongside a nested wizard used to unmount portals mid-render
  // and cause a React 18 `insertBefore` / `removeChild` crash. Instead we close this dialog first
  // via onOpenChange(false), then open the wizard.
  const openWizard = () => {
    // 1) close this dialog cleanly (state flip through parent)
    onOpenChange(false);
    // 2) open wizard on the next tick so portals unmount fully first
    setTimeout(() => setWizardOpen(true), 60);
  };

  return (
    <>
      <Dialog open={!!open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="boost-post-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5 text-primary" />
              Impulsione este conteúdo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {media && (
              <div className="rounded-lg overflow-hidden border max-h-48">
                {isVideo
                  ? <video src={media} className="w-full h-48 object-cover" muted />
                  : <img src={media} alt="preview" className="w-full h-48 object-cover" />}
              </div>
            )}
            <p className="text-sm">
              Transforme este post em anúncio pago em poucos cliques. Chegue em milhares de pessoas com
              o <b>leilão justo</b> do FUTURO ADS — sem preços abusivos.
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
              <li>Foto/vídeo pré-preenchido</li>
              <li>Teto de preço garantido por objetivo</li>
              <li>Segmente por interesses, cidade e idade</li>
            </ul>
          </div>
          <DialogFooter className="flex gap-2 sm:flex-row-reverse">
            <Button onClick={openWizard} data-testid="boost-open-wizard">
              <Rocket className="w-4 h-4 mr-1" /> Criar campanha
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="boost-dismiss">
              <X className="w-4 h-4 mr-1" /> Agora não
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AdsWizard
        open={wizardOpen}
        onOpenChange={(v) => setWizardOpen(!!v)}
        onDone={() => setWizardOpen(false)}
        initialAd={prefill}
      />
    </>
  );
}
